import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Check, ArrowRight, ShieldCheck, LockOpen, Eye, Minus, CircleNotch, Star,
} from "@phosphor-icons/react";
import Logomark from "@/components/Logomark";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";

// ----------------------------------------------------------------------------
// Xartis Pass — single tier, time-based access (AUD, GST-inclusive; GST = 1/11).
// Real Stripe Checkout is wired via POST /api/payments/checkout.
// ----------------------------------------------------------------------------
const PASS_FEATURES = (builds) => [
  `${builds} full AI plan builds included`,
  "Unlimited Atlas AI co-pilot refinement",
  "Server-side PDF & editable DOCX exports",
  "Secure parse-and-purge document uploads",
  "All horizon templates — Sprint, 30-60-90, Macro Vision",
  "Unlimited boards, sub-tasks & plan library",
];

const PASSES = [
  { id: "pass_30", title: "30-DAY PASS", term: "30 DAYS", total: 29, base: 26.36, gst: 2.64, builds: 5, perMo: null, highlight: false },
  { id: "pass_90", title: "90-DAY PASS", term: "90 DAYS", total: 69, base: 62.73, gst: 6.27, builds: 15, perMo: 23, save: "Save ~20%", highlight: true },
  { id: "pass_365", title: "ANNUAL PASS", term: "365 DAYS", total: 199, base: 180.91, gst: 18.09, builds: 60, perMo: 17, save: "Save ~43%", highlight: false },
];

const FREE_FEATURES = [
  { t: "1 free plan build — your own context", ok: true },
  { t: "Full board & sub-task progress tracking", ok: true },
  { t: "In-plan Atlas AI co-pilot", ok: false },
  { t: "PDF / DOCX export", ok: false },
];

const money = (n) => `$${n.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function PassCard({ p, onBuy, buying }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
      data-testid={`pass-card-${p.id}`}
      className={`relative flex flex-col border p-7 bg-white ${p.highlight ? "border-gray-950 shadow-[0_18px_50px_rgba(0,0,0,0.12)]" : "border-gray-200"}`}
    >
      {p.highlight && (
        <span className="absolute -top-3 left-7 bg-gray-950 text-white text-[10px] font-bold uppercase tracking-[0.18em] px-3 py-1 inline-flex items-center gap-1"
          data-testid="pass-badge-sweet-spot"><Star size={11} weight="fill" /> Sweet spot</span>
      )}
      <div className="flex items-baseline gap-2 mb-1">
        <h3 className="font-display font-black text-xl tracking-tight">{p.title}</h3>
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-gray-400">// {p.term}</span>
      </div>
      <div className="mb-5 min-h-[30px] flex items-start">
        <span className="inline-block text-[10px] font-bold uppercase tracking-[0.14em] px-2 py-1"
          style={{ color: p.highlight ? "#fff" : "#0a0a0a", backgroundColor: p.highlight ? "#0a0a0a" : "#f3f4f6" }}>
          FULL ACTIVE ACCESS · {p.builds} BUILDS
        </span>
      </div>

      <div className="mb-5">
        <div className="flex items-baseline gap-1.5">
          <span className="font-display font-black text-5xl tracking-tight">${p.total}</span>
          <span className="text-sm font-semibold text-gray-500">AUD / {p.term.toLowerCase()}</span>
        </div>
        <p className="font-mono text-[11px] text-gray-500 mt-2">
          Base {money(p.base)} <span className="text-gray-300">+</span> 10% GST {money(p.gst)}
          <span className="text-gray-300"> = </span><b className="text-gray-700">{money(p.total)}</b> inc. GST
        </p>
        {p.perMo && (
          <p className="font-mono text-[11px] text-emerald-700 mt-1">Equivalent to ~${p.perMo}/mo · {p.save}</p>
        )}
      </div>

      <ul className="space-y-2.5 mb-6 flex-1">
        {PASS_FEATURES(p.builds).map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm text-gray-700 leading-snug">
            <Check size={16} weight="bold" className="mt-0.5 shrink-0" style={{ color: p.highlight ? "#0a0a0a" : "#6b7280" }} />
            {f}
          </li>
        ))}
      </ul>

      <div className="flex items-start gap-2 border-t border-gray-100 pt-4 mb-5">
        <ShieldCheck size={16} weight="duotone" className="mt-0.5 shrink-0 text-emerald-700" />
        <p className="text-xs text-gray-500 leading-relaxed">
          <b className="text-gray-700">No auto-renew.</b> When your pass ends you drop to the Read-Only Vault — your plans are never deleted.
        </p>
      </div>

      <button onClick={() => onBuy(p.id)} disabled={!!buying} data-testid={`pass-cta-${p.id}`}
        className={`w-full inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-bold transition-colors disabled:opacity-60 ${
          p.highlight ? "bg-gray-950 text-white hover:bg-gray-700" : "border border-gray-900 text-gray-900 hover:bg-gray-50"
        }`}>
        {buying === p.id ? (<><CircleNotch size={15} className="animate-spin" /> Redirecting…</>) : (<>Buy {p.title.toLowerCase().replace(" pass", "")} pass <ArrowRight size={15} weight="bold" /></>)}
      </button>
    </motion.div>
  );
}

function FreeCard({ onStart }) {
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
      data-testid="pass-card-free" className="relative flex flex-col border border-dashed border-gray-300 p-7 bg-gray-50/60">
      <div className="flex items-baseline gap-2 mb-1">
        <h3 className="font-display font-black text-xl tracking-tight">FREE TASTER</h3>
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-gray-400">// ONE-OFF</span>
      </div>
      <div className="mb-5 min-h-[30px] flex items-start">
        <span className="inline-block text-[10px] font-bold uppercase tracking-[0.14em] px-2 py-1 bg-gray-200 text-gray-700">
          ONE PLAN BUILD, ON THE HOUSE
        </span>
      </div>
      <div className="mb-5">
        <div className="flex items-baseline gap-1.5">
          <span className="font-display font-black text-5xl tracking-tight">$0</span>
          <span className="text-sm font-semibold text-gray-500">AUD</span>
        </div>
        <p className="font-mono text-[11px] text-gray-500 mt-2">No card required · no GST</p>
      </div>
      <ul className="space-y-2.5 mb-6 flex-1">
        {FREE_FEATURES.map((f) => (
          <li key={f.t} className={`flex items-start gap-2.5 text-sm leading-snug ${f.ok ? "text-gray-700" : "text-gray-400"}`}>
            {f.ok
              ? <Check size={16} weight="bold" className="mt-0.5 shrink-0 text-gray-500" />
              : <Minus size={16} weight="bold" className="mt-0.5 shrink-0 text-gray-300" />}
            <span className={f.ok ? "" : "line-through decoration-gray-300"}>{f.t}</span>
          </li>
        ))}
      </ul>
      <div className="flex items-start gap-2 border-t border-gray-100 pt-4 mb-5">
        <ShieldCheck size={16} weight="duotone" className="mt-0.5 shrink-0 text-emerald-700" />
        <p className="text-xs text-gray-500 leading-relaxed">
          <b className="text-gray-700">Yours forever:</b> kept in your Read-Only Vault — buy a pass any time to edit, add Atlas & export.
        </p>
      </div>
      <button onClick={onStart} data-testid="pass-cta-free"
        className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-bold border border-gray-900 text-gray-900 hover:bg-gray-100 transition-colors">
        Start free build <ArrowRight size={15} weight="bold" />
      </button>
    </motion.div>
  );
}

function AccessLogic() {
  return (
    <div className="grid sm:grid-cols-2 gap-4 mt-14 max-w-4xl mx-auto" data-testid="access-logic">
      <div className="border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-2">
          <LockOpen size={18} weight="duotone" className="text-emerald-700" />
          <h4 className="font-display font-bold text-base">Active Access Pass</h4>
        </div>
        <p className="text-sm text-gray-600 leading-relaxed">
          While your pass is live you get <b>full active system access</b> — metered plan builds, unlimited
          edits and Atlas refinement, plus server-side PDF/DOCX exports. Buying another pass extends your
          time and stacks your remaining builds.
        </p>
      </div>
      <div className="border border-gray-200 p-6 bg-amber-50/60">
        <div className="flex items-center gap-2 mb-2">
          <Eye size={18} weight="duotone" className="text-amber-700" />
          <h4 className="font-display font-bold text-base">Read-Only Vault</h4>
        </div>
        <p className="text-sm text-gray-600 leading-relaxed">
          When a pass expires your account reverts to a <b>free read-only state</b>. Log in any time to view,
          read and browse every past plan — editing, new builds and exports simply unlock again the moment
          you buy a new pass.
        </p>
      </div>
    </div>
  );
}

export default function Pricing() {
  const navigate = useNavigate();
  const { user, loginWithGoogle } = useAuth();
  const [buying, setBuying] = useState(null);

  const buy = async (packageId) => {
    if (!user) {
      toast.info("Sign in with Google first — then pick your pass.");
      loginWithGoogle();
      return;
    }
    setBuying(packageId);
    try {
      const { checkout_url } = await api.checkout(packageId);
      window.location.href = checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout. Please try again.");
      setBuying(null);
    }
  };

  const passActive = user && user.tier === "full" && user.pass_expires_at;
  const expiryLabel = passActive
    ? new Date(user.pass_expires_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })
    : null;

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="pricing-page">
      <header className="border-b border-gray-200 px-6 sm:px-10 py-4 flex items-center justify-between">
        <button onClick={() => navigate(user ? "/library" : "/")} className="flex items-center gap-2" data-testid="pricing-home-link">
          <Logomark size={24} />
          <span className="font-display font-black text-2xl tracking-tight">Xartis</span>
        </button>
        <button onClick={() => navigate("/start")} data-testid="pricing-skip-link"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-600 hover:text-gray-900 transition-colors">
          Start a plan <ArrowRight size={15} weight="bold" />
        </button>
      </header>

      <main className="flex-1 w-full max-w-6xl mx-auto px-6 sm:px-10 py-14">
        <div className="text-center">
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-gray-500 mb-3">Start free · Pay as you go · No auto-renew</p>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-[1.05]">One pass. Everything unlocked.<br />Keep your plans forever.</h1>
          <p className="mt-5 text-base text-gray-600 leading-relaxed max-w-2xl mx-auto">
            Start with a free plan build, then buy a single Xartis Pass — every pass unlocks the full toolkit
            for a fixed window. When it ends nothing is deleted or locked away — your account simply drops to
            a read-only vault until you top up again. All prices in <b>Australian Dollars (AUD)</b>, inclusive of <b>10% GST</b>.
          </p>
          {passActive && (
            <div className="mt-6 inline-flex items-center gap-2 border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm text-emerald-900" data-testid="active-pass-banner">
              <ShieldCheck size={16} weight="duotone" className="text-emerald-700" />
              <span><b>{user.pass_label || "Pass"} active</b> until {expiryLabel} · {user.builds_remaining == null ? "unlimited" : user.builds_remaining} builds left. Buying again extends your time and stacks builds.</span>
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-4 gap-5 mt-12 items-stretch">
          <FreeCard onStart={() => navigate("/start")} />
          {PASSES.map((p) => <PassCard key={p.id} p={p} onBuy={buy} buying={buying} />)}
        </div>

        <AccessLogic />

        <p className="text-center font-mono text-[11px] text-gray-400 mt-10 max-w-xl mx-auto">
          Payments are processed securely by Stripe. Passes are one-off purchases — no subscription, no auto-renew.
        </p>
      </main>
    </div>
  );
}
